import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'cva';
import { cn } from '@seller-hermes/utils/cn';
import { Loader2 } from 'lucide-react';

const buttonVariants = cva({
  base: cn(
    'inline-flex items-center justify-center gap-x-2 rounded-md text-sm ring-offset-background transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
  ),
  variants: {
    variant: {
      default: 'bg-primary text-primary-foreground hover:bg-primary/90',
      destructive:
        'bg-destructive text-destructive-foreground hover:bg-destructive/90',
      outline:
        'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
      secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
      ghost: 'hover:bg-accent hover:text-accent-foreground',
      link: 'text-primary underline-offset-4 hover:underline',
    },
    size: {
      default: 'px-3 py-2',
      sm: 'h-9 rounded-sm px-3',
      lg: 'h-11 rounded-sm px-8',
      icon: 'h-10 w-10',
      tight: 'h-8 px-3 py-2',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    const renderInner = () => {
      if (isLoading) {
        return (
          <React.Fragment>
            <Loader2 className="h-4 w-4 animate-spin" />
            {children}
          </React.Fragment>
        );
      }
      return children;
    };

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        type={props.type || 'button'}
      >
        {renderInner()}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
